import { TellescopeModelFromName } from "@tellescope/tanstack-query";
import { Button, Input } from "@tellescope/ui";
import { Select } from "@tellescope/design-system";
import { DndContext, type DragEndEvent, PointerSensor, closestCenter, useSensor, useSensors } from "@dnd-kit/core";
import { SortableContext, arrayMove, verticalListSortingStrategy } from "@dnd-kit/sortable";
import { ArrowLeft, Info, Plus } from "lucide-react";
import { useTranslation } from "react-i18next";
import { type RangeRow, useVitalConfigurationForm } from "./useVitalConfigurationForm";
import { UNIT_OPTIONS } from "./vitalConfigurationConstants";
import { SortableRangeRow } from "./SortableRangeRow";

export interface VitalConfigurationFormProps {
  config: TellescopeModelFromName<"vital_configurations"> | null;
  onSave: (data: {
    title: string;
    unit: string;
    ranges: TellescopeModelFromName<"vital_configurations">["ranges"];
  }) => Promise<void>;
  onCancel: () => void;
}

export const VitalConfigurationForm = ({ config, onSave, onCancel }: VitalConfigurationFormProps) => {
  const { t } = useTranslation("vitals");
  const {
    title,
    setTitle,
    unit,
    setUnit,
    ranges,
    rangesError,
    addRange,
    removeRange,
    updateRange,
    reorderRanges,
    handleSave,
  } = useVitalConfigurationForm(config, onSave, onCancel);

  const sensors = useSensors(useSensor(PointerSensor));

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;
    if (over && active.id !== over.id) {
      const oldIndex = ranges.findIndex((r: RangeRow) => r.id === active.id);
      const newIndex = ranges.findIndex((r: RangeRow) => r.id === over.id);
      reorderRanges(arrayMove(ranges, oldIndex, newIndex));
    }
  };

  return (
    <div className="flex flex-col gap-6 p-6">
      <div className="flex items-center gap-2">
        <Button variant="ghost" size="icon" className="h-7 w-7 text-muted-foreground" onClick={onCancel}>
          <ArrowLeft className="h-4 w-4" />
        </Button>
        <h2 className="text-lg font-semibold">
          {config ? t("Edit Vital Configuration") : t("Create Vital Configuration")}
        </h2>
      </div>

      <div className="flex flex-col gap-2">
        <label className="text-sm font-medium">{t("Configuration Title")}</label>
        <Input placeholder={t("e.g., Blood Pressure")} value={title} onChange={(e) => setTitle(e.target.value)} />
      </div>

      <div className="flex flex-col gap-2">
        <label className="text-sm font-medium">{t("Unit of Measurement")}</label>
        <Select value={unit} onValueChange={setUnit}>
          <Select.Trigger>
            <Select.Value placeholder={t("Select unit...")} />
          </Select.Trigger>
          <Select.Content>
            {UNIT_OPTIONS.map((u) => (
              <Select.Item key={u} value={u}>
                {u}
              </Select.Item>
            ))}
          </Select.Content>
        </Select>
      </div>

      <div className="rounded-md bg-muted/50 border p-4 flex gap-3">
        <Info className="h-4 w-4 text-blue-500 mt-0.5 shrink-0" />
        <div className="flex flex-col gap-1">
          <span className="text-sm font-medium">{t("Range Tips")}</span>
          <ul className="text-sm text-muted-foreground list-disc list-inside space-y-1">
            <li>
              {t(
                "Ranges are evaluated in order from top to bottom until a match is found. Order according to priority."
              )}
            </li>
            <li>{t("With trends, a positive value represents an increase over time.")}</li>
            <li>{t("With trends, a negative value represents a decrease over time.")}</li>
          </ul>
        </div>
      </div>

      <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
        <SortableContext items={ranges.map((r: RangeRow) => r.id)} strategy={verticalListSortingStrategy}>
          <div className="flex flex-col gap-2">
            {ranges.map((row: RangeRow) => (
              <SortableRangeRow key={row.id} row={row} onRemove={removeRange} onChange={updateRange} />
            ))}
          </div>
        </SortableContext>
      </DndContext>

      <Button variant="outline" className="w-full border-dashed" onClick={addRange}>
        <Plus className="h-4 w-4" />
        {t("Add Range")}
      </Button>

      {rangesError && <p className="text-sm text-destructive">{rangesError}</p>}

      <div className="flex justify-end gap-2">
        <Button variant="outline" onClick={onCancel}>
          {t("Cancel")}
        </Button>
        <Button onClick={handleSave} disabled={!title || !unit}>
          {t("Save")}
        </Button>
      </div>
    </div>
  );
};
